import React from 'react';

// Vocabulaire visuel de l'espace Pro.
//
// L'écran était bavard : un emoji par onglet, un fond saturé par statut, la
// même bordure et la même ombre sur chaque bloc — de quoi aplatir toute
// hiérarchie. Ici, une seule échelle typographique, l'orange Apex réservé à
// l'action principale, et la couleur d'état portée par une pastille et du
// texte plutôt que par un pavé coloré.

// ─── Icônes ─────────────────────────────────────────────────────────────────
// Un trait de 1,5 px sur une grille de 24 : lisible à 16 px, discret à côté
// d'un libellé, et qui ne change pas de style d'un écran à l'autre.
const CHEMINS = {
  pipeline:  'M4 6h16M4 12h10M4 18h6',
  plus:      'M12 5v14M5 12h14',
  micro:     'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM5 11a7 7 0 0 0 14 0M12 18v3',
  chantier:  'M3 21h18M5 21V8l7-4 7 4v13M10 21v-6h4v6',
  euro:      'M15 6.5A5.5 5.5 0 0 0 9 12a5.5 5.5 0 0 0 6 5.5M5 10.5h7M5 13.5h7',
  equipe:    'M16 19v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6M21 19v-2a4 4 0 0 0-3-3.9M16 5.1a4 4 0 0 1 0 7.8',
  cle:       'M15 7a4 4 0 1 1-3.5 5.9L4 20.4V17H7v-3h3l1.5-1.5A4 4 0 0 1 15 7z',
  document:  'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5zM14 3v5h5',
  facture:   'M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6',
  avoir:     'M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3',
  photo:     'M4 7h3l2-2h6l2 2h3v12H4V7zM12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  video:     'M3 7h11v10H3V7zM14 10l7-3v10l-7-3',
  web:       'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z',
  fleche:    'M5 12h14M13 6l6 6-6 6',
  telecharger: 'M12 4v10M8 11l4 4 4-4M5 19h14',
};

export function Icone({ nom, className = 'w-4 h-4' }) {
  const d = CHEMINS[nom];
  if (!d) return null;
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// ─── Structure de page ──────────────────────────────────────────────────────
export function Page({ children, large, className = '' }) {
  // Les écrans de lecture se tiennent à une largeur confortable ; le kanban et
  // les plannings, eux, prennent tout l'espace que l'écran leur laisse.
  return <div className={`${large ? '' : 'max-w-6xl'} ${className}`.trim()}>{children}</div>;
}

/** Titre, sous-titre explicatif, et l'action principale de l'écran à droite. */
export function EnTetePage({ titre, sous, children }) {
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 mb-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-slate-900">{titre}</h1>
        {sous && <p className="text-sm text-slate-500 mt-1">{sous}</p>}
      </div>
      {children && <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/** Un bloc de contenu. `plat` retire la bordure quand le bloc n'est pas un objet séparé. */
export function Bloc({ titre, action, children, plat = false, className = '' }) {
  return (
    <section className={`${plat ? '' : 'bg-white border border-slate-200 rounded-xl shadow-sm'} ${className}`}>
      {(titre || action) && (
        <header className="flex items-center gap-3 px-4 pt-3.5 pb-2.5">
          <h2 className="text-[13px] font-semibold text-slate-800">{titre}</h2>
          {action && <div className="ml-auto flex items-center gap-2">{action}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Etiquette({ children, className = '' }) {
  return (
    <span className={`block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 ${className}`}>
      {children}
    </span>
  );
}

// ─── Actions ────────────────────────────────────────────────────────────────
// Trois niveaux, pas davantage : ce qu'on fait, ce qu'on peut faire, ce qu'on
// fait rarement. L'orange n'est porté que par le premier.
const STYLES_BOUTON = {
  primaire: 'bg-orange-600 hover:bg-orange-700 text-white border-transparent',
  neutre:   'bg-white hover:bg-slate-50 text-slate-700 border-slate-300',
  sombre:   'bg-slate-900 hover:bg-slate-800 text-white border-transparent',
  discret:  'bg-transparent hover:bg-slate-100 text-slate-500 border-transparent',
  danger:   'bg-white hover:bg-red-50 text-red-700 border-red-200',
};

export function Bouton({ variante = 'neutre', taille = 'normal', icone, children, className = '', ...props }) {
  const dims = taille === 'petit' ? 'text-xs px-2.5 py-1.5 gap-1.5' : 'text-sm px-3.5 py-2 gap-2';
  return (
    <button
      className={`inline-flex items-center justify-center font-medium rounded-lg border transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap
                  ${STYLES_BOUTON[variante]} ${dims} ${className}`}
      {...props}
    >
      {icone && <Icone nom={icone} className={taille === 'petit' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />}
      {children}
    </button>
  );
}

// ─── État ───────────────────────────────────────────────────────────────────
// Une pastille et un mot. Un statut n'a pas besoin d'un pavé de couleur pour
// se faire voir, et trois pavés côte à côte ne se hiérarchisent plus.
const TONS = {
  neutre:  { point: 'bg-slate-300',   texte: 'text-slate-500' },
  attente: { point: 'bg-amber-500',   texte: 'text-amber-700' },
  cours:   { point: 'bg-blue-500',    texte: 'text-blue-700' },
  fait:    { point: 'bg-emerald-500', texte: 'text-emerald-700' },
  alerte:  { point: 'bg-red-500',     texte: 'text-red-700' },
};

export function Etat({ ton = 'neutre', children, className = '' }) {
  const t = TONS[ton] || TONS.neutre;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${t.texte} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.point}`} />
      {children}
    </span>
  );
}

export function Pastille({ ton = 'neutre', className = '' }) {
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${(TONS[ton] || TONS.neutre).point} ${className}`} />;
}

/** Mention discrète sur un élément : « interne », « brouillon », « 3 doc. ». */
export function Mention({ children, ton = 'neutre' }) {
  const t = ton === 'attente' ? 'bg-amber-50 text-amber-700 border-amber-200'
          : ton === 'fait' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : ton === 'alerte' ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-slate-50 text-slate-500 border-slate-200';
  return <span className={`inline-block text-[11px] font-medium border rounded px-1.5 py-0.5 ${t}`}>{children}</span>;
}

export function Vide({ children, className = '' }) {
  return (
    <p className={`text-sm text-slate-400 px-4 py-8 text-center ${className}`}>{children}</p>
  );
}

/** Montant aligné sur la colonne, toujours en chiffres à chasse fixe. */
export function Montant({ valeur, className = '' }) {
  return (
    <span className={`tabular-nums ${className}`}>
      {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(valeur || 0))} €
    </span>
  );
}

export function Toast({ message }) {
  if (!message) return null;
  return (
    <div role="status"
         className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg text-sm font-medium
                     text-white shadow-lg ${message.type === 'err' ? 'bg-red-700' : 'bg-slate-900'}`}>
      {message.texte}
    </div>
  );
}
